import * as IoC from 'typescript-ioc';
import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { FormVmValueType, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../../../../infrastructure/implementations/form-viewmodel-controller';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import { FieldKey, configList as connectionsEmailConfigList } from './send-connections-email-form.config';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { EmailTemplateTypes, IEmailTemplateField, IEmailTemplateFieldRequestParams, IEmailTemplatesDAO } from '../../../../../../api/email-templates.dao';
import { getCommaSeparatedStringList } from '../../../../../../infrastructure/utils/common.utils';
import { IOption } from '../../../../../global/select/select';
import { emailTemplateTypes } from '../../../../../../constants/email-templates-label.constants';
import { MeetingConnectionsFormVM } from '../meeting-connections-form.viewmodel';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import Text from '../../../../../global/text/text';
import { Space } from '../../../../../../constants/html-character-entities';
import { BCC_TEXT, CC_TEXT, EMAIL_SENT_SUCCESSFULLY, SEND_CONNECTION_EMAILS_TEXT, TO_TEXT, YES_SEND } from './send-connections-email-form.texts';
import { renderEmails } from '../../../../../../infrastructure/utils/connections.utils';
import { EMAIL_TEMPLATES_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';

export type FormFieldProps = {
    value: FormVmValueType;
    valid: boolean;
    touched: boolean;
    setValue: (value: FormVmValueType) => void;
    setTouched: () => void;
    validationMessage: MessageDescriptor | null;
};

class SendConnectionsEmailFormViewmodel {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    protected static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'SendConnectionsEmailFormViewmodel', connectionsEmailConfigList);
            }
        }();
    }

    @observable
    private formDataCtrl: AbstractFormViewModelController;
    @observable
    public isLoading: boolean;
    @observable
    private isSending = false;

    constructor(
        private readonly meetingConnectionsFormVM,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly emailTemplatesDAO: IEmailTemplatesDAO = IoC.Container.get(IEmailTemplatesDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.meetingConnectionsFormVm.initConnectionsEmailModal = this.initialize;
    }
    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public get loading(): boolean { return this.isLoading; }
    public get sending(): boolean { return this.isSending; }
    public get meetingConnectionsFormVm(): MeetingConnectionsFormVM { return this.meetingConnectionsFormVM; }
    public get emailTemplates(): IEmailTemplateField[] { return this.meetingConnectionsFormVm.emailTemplates; }
    public get selectedOption(): IEmailTemplateField { return this.meetingConnectionsFormVm.selectedEmailTemplate; }

    public get formVM(): AbstractFormViewModelController {
        return this.formDataCtrl;
    }
    public actionButtonText = (): string => {
        return this.intl.formatMessage(SEND_CONNECTION_EMAILS_TEXT);
    };

    public submitForm = async (): Promise<void> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
        } else {
            this.sendEmailConfirmationPopup();
        }
    };

    public getFormFieldPropsByKey(key: string): FormFieldProps {
        return {
            value: this.formDataCtrl.getValue(key),
            setValue: value => this.formDataCtrl.setValue(key, value),
            setTouched: () => this.formDataCtrl.setValueTouched(key),
            ...this.formDataCtrl.getValueValidationState(key)

        };
    }

    // For the destructing in view. As a key here - value in FormField enum.
    public getFormFields(keys: TObject<string>): TObject<FormFieldProps> {
        const formFields = {};

        for (const key of Object.values(keys)) {
            formFields[key] = this.getFormFieldPropsByKey(key);
        }

        return formFields;
    }

    public getEmailTypes = (): IOption[] => Object.values(EmailTemplateTypes).map(type => ({
            value: type,
            label: this.intl.formatMessage(emailTemplateTypes[type])
        }))
    ;
    public getSelectedEmails = (field: FormFieldProps): string[] => {
        return typeof field.value === 'string'
            ? field.value.split(',')
            : field.value as string[];
    };

    @action
    public initialize = async (): Promise<void> => {
        try {
            runInAction(() => {
                this.isLoading = true;
            });
            const response = await this.meetingConnectionsFormVm.getMeetingTemplateById();
            const transpiledEmailBody = transpileInlineStylesToClasses(response.body);
            const initialValues = this.composeTemplateInitialProps({...response, body: transpiledEmailBody});
            runInAction(() => {
                this.formDataCtrl = SendConnectionsEmailFormViewmodel.createFormVM(initialValues);
            });
            this.meetingConnectionsFormVm.trackSendConnectionEmailsEvents(
                EMAIL_TEMPLATES_EVENTS.GENERATE_CONNECTIONS_EMAIL_TEXT
            );
        } catch (error) {
            SendConnectionsEmailFormViewmodel.handleApiException(error);
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    public sendEmailConfirmationPopup = () => {
        const dto = this.toEmailTemplateDTO();
        this.alertsService.confirm({
            className: 'send-email-confirm-popup',
            message: (
                <>
                    <Text
                        tag='div'
                        className='alert-message'
                        id='oe.sendConnection.alerts.sendThisEmailToTheFollowingRecipients'
                        description='Alert message'
                        message='You are about to send this email to the following recipients:'
                    />
                    <br />
                    <div>
                        { this.intl.formatMessage(TO_TEXT) }: <Space />
                        { renderEmails(dto.to) }
                    </div>
                    { !!dto.cc.length && <>
                         <br />
                         <div>
                            { this.intl.formatMessage(CC_TEXT) }: <Space />
                            { renderEmails(dto.cc) }
                        </div>
                    </>}
                    { !!dto.bcc.length && <>
                        <br />
                        <div>
                            { this.intl.formatMessage(BCC_TEXT) }: <Space />
                            { renderEmails(dto.bcc) }
                        </div>
                    </>}
                    <br />
                    <Text
                        tag='div'
                        className='alert-message'
                        id='oe.sendConnection.alerts.sendThisEmailToTheFollowingRecipients'
                        description='Alert message'
                        message={`To send the email, click \'Yes, Send\'. To go back, click \'Cancel\'.`}
                    />
                </>
            ),
            confirmButtonProps: {
                text: this.intl.formatMessage(YES_SEND),
                modifier: 'confirm-warn',
                handler: () => this.sendConnectionEmail(dto),
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private toEmailTemplateDTO = (): IEmailTemplateFieldRequestParams => {
        return {
            accountId: this.meetingConnectionsFormVm.meetingApiParams.accountId,
            id: this.selectedOption.id,
            name: this.selectedOption.name,
            description: this.selectedOption.description,
            type: this.selectedOption.type,
            to: getCommaSeparatedStringList(this.formDataCtrl.getValue(FieldKey.TO) as string),
            replyTo: getCommaSeparatedStringList(this.formDataCtrl.getValue(FieldKey.REPLY_TO) as string),
            cc: getCommaSeparatedStringList(this.formDataCtrl.getValue(FieldKey.CC) as string),
            bcc: getCommaSeparatedStringList(this.formDataCtrl.getValue(FieldKey.BCC) as string),
            subject: (this.formDataCtrl.getValue(FieldKey.EMAIL_SUBJECT) as string).trim(),
            body: (this.formDataCtrl.getValue(FieldKey.EMAIL_BODY) as string),
        } as IEmailTemplateFieldRequestParams;
    };

    private sendConnectionEmail = async (dto: IEmailTemplateFieldRequestParams): Promise<void> => {
        try {
            runInAction(() => {
                this.isSending = true;
            });
            const body = transpileClassesToInlineStyles(dto.body);
            await this.emailTemplatesDAO.sendEmail({ ...dto, body: body });
            showNotification(this.intl.formatMessage(EMAIL_SENT_SUCCESSFULLY));
            this.meetingConnectionsFormVm.trackSendConnectionEmailsEvents(
                EMAIL_TEMPLATES_EVENTS.SEND_CONNECTIONS_EMAIL,
                {
                    TO: dto.to.length,
                    CC: dto.cc.length,
                    BCC: dto.bcc.length
                }
            );
            this.meetingConnectionsFormVm.closeSendConnectionEmail();
        } catch (e) {
            SendConnectionsEmailFormViewmodel.handleApiException(e);
        } finally {
            runInAction(() => {
                this.isSending = false;
            });
        }
    };

    private composeTemplateInitialProps = (template: IEmailTemplateField): InitialData => {
        return {
                [FieldKey.TO]: template.to.join(','),
                [FieldKey.REPLY_TO]: template.replyTo.join(','),
                [FieldKey.CC]: template.cc.join(','),
                [FieldKey.BCC]: template.bcc.join(','),
                [FieldKey.EMAIL_SUBJECT]: template.subject,
                [FieldKey.EMAIL_BODY]: template.body,
            }
        ;
    };
}

export { SendConnectionsEmailFormViewmodel };
